import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import { Box, Typography } from '@mui/material';
import moment from 'moment';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import { getCustomerDataReport } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';

const { RangePicker } = DatePicker;

const filterSelectStyles = reportFilterSelectStyles;

const REPORT_COLUMNS = [
    { fieldName: 'customer_name', headerName: 'Customer Name' },
    { fieldName: 'project_name', headerName: 'Project Name' },
    { fieldName: 'site_address', headerName: 'Site Address' },
    { fieldName: 'contact_person', headerName: 'Contact Person' },
    { fieldName: 'mobile_numbers', headerName: 'Mobile Nos' },
    { fieldName: 'billing_name', headerName: 'Billing Name' },
    { fieldName: 'billing_address', headerName: 'Address' },
    { fieldName: 'state', headerName: 'State' },
    { fieldName: 'pan_no', headerName: 'PAN No' },
    { fieldName: 'gst_no', headerName: 'GST No' },
    { fieldName: 'contact_details', headerName: 'Contact Details' },
    { fieldName: 'email', headerName: 'Email' },
    { fieldName: 'credit_limit', headerName: 'Credit Limit', align: 'right' },
    { fieldName: 'customer_type', headerName: 'Type of Customer' },
    { fieldName: 'nature_of_business', headerName: 'Nature of Business' },
    { fieldName: 'marketing_name', headerName: 'Marketing Name' },
];

function resolveMultiIds(selected) {
    if (!selected || selected.length === 0) {
        return '';
    }
    if (selected.some((item) => item.value === 'select-all')) {
        return '*';
    }
    return selected.map((item) => item.value).join(',');
}

function formatAmount(value) {
    return Number(value || 0).toFixed(2);
}

function CustomerDataReport() {
    const [cookies] = useCookies(['myToken']);
    const [records, setRecords] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [selectedSalesrep, setSelectedSalesrep] = useState([]);
    const [fromDate, setFromDate] = useState(moment().subtract(5, 'years').startOf('year'));
    const [toDate, setToDate] = useState(moment());
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [hasGenerated, setHasGenerated] = useState(false);

    const customersWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All customers' }, ...customers],
        [customers],
    );

    const salesrepsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All sales reps' }, ...salesreps],
        [salesreps],
    );

    useEffect(() => {
        getAllSalesRepresentatives(cookies).then((salesRepList) => {
            setSalesreps((salesRepList.salesrep_data || [])
                .filter((item) => item.status === true || item.status === 1 || item.status === 'Active')
                .map((sales) => ({ value: sales.id, label: sales.entity_name })));
        });
        getAllCustomerMain(cookies).then((customerList) => {
            setCustomers((customerList.customer_list || [])
                .filter((item) => item.status === true || item.status === 1 || item.status === 'Active')
                .map((customer) => ({ value: customer.id, label: customer.name })));
        });
    }, [cookies]);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return records;
        }
        return records.filter((row) => REPORT_COLUMNS.some((col) => {
            const value = row[col.fieldName];
            return value !== null && value !== undefined && String(value).toLowerCase().includes(query);
        }));
    }, [records, searchText]);

    const summaryCards = useMemo(() => {
        if (!hasGenerated || !filteredRecords.length) {
            return [];
        }

        const customerKeys = new Set();
        const projectNames = new Set();
        const creditByCustomer = new Map();

        filteredRecords.forEach((row) => {
            const customerKey = row.id ?? row.customer_name;
            if (customerKey !== null && customerKey !== undefined && customerKey !== '') {
                customerKeys.add(String(customerKey));
                if (!creditByCustomer.has(customerKey)) {
                    creditByCustomer.set(customerKey, Number(row.credit_limit) || 0);
                }
            }
            const projectName = (row.project_name || '').trim();
            if (projectName) {
                projectNames.add(projectName);
            }
        });

        const totalCreditLimit = Array.from(creditByCustomer.values())
            .reduce((sum, value) => sum + value, 0);

        return [
            { label: 'Sites', value: String(filteredRecords.length) },
            { label: 'Customers', value: String(customerKeys.size) },
            { label: 'Projects', value: String(projectNames.size) },
            { label: 'Credit Limit', value: formatAmount(totalCreditLimit) },
        ];
    }, [filteredRecords, hasGenerated]);

    const exportFileBase = useMemo(() => {
        const datePart = fromDate?.isValid() && toDate?.isValid()
            ? `${fromDate.format('DD-MM-YYYY')}_to_${toDate.format('DD-MM-YYYY')}`
            : moment().format('DD-MM-YYYY');
        return `CustomerDataReport_${datePart}`;
    }, [fromDate, toDate]);

    const pdfSummaryLines = useMemo(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            return [];
        }
        return [`Period: ${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')}`];
    }, [fromDate, toDate]);

    const loadReport = useCallback(() => {
        if (!fromDate?.isValid() || !toDate?.isValid()) {
            displayError({ message: 'Please select a valid date range.' }, 'Report Failed');
            return Promise.resolve();
        }

        setIsLoading(true);
        const params = {
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
        };
        const salesrepIds = resolveMultiIds(selectedSalesrep);
        const customerIds = resolveMultiIds(selectedCustomer);
        if (salesrepIds) {
            params.salesrep_ids = salesrepIds;
        }
        if (customerIds) {
            params.customer_ids = customerIds;
        }

        return getCustomerDataReport(cookies, params)
            .then((response) => {
                setRecords(response.customer_data_report_list || []);
                setHasGenerated(true);
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Report Failed');
                setRecords([]);
            })
            .finally(() => setIsLoading(false));
    }, [cookies, fromDate, selectedCustomer, selectedSalesrep, toDate]);

    const ensureExportData = () => {
        if (!hasGenerated || !filteredRecords.length) {
            displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
            return false;
        }
        return true;
    };

    const handleCSVExport = () => {
        if (!ensureExportData()) return;
        exportAsCSV(REPORT_COLUMNS, filteredRecords, exportFileBase);
    };

    const handleExcelExport = () => {
        if (!ensureExportData()) return;
        exportAsExcel(REPORT_COLUMNS, filteredRecords, exportFileBase);
    };

    const handlePDFExport = async () => {
        if (!ensureExportData()) return;
        setIsLoading(true);
        try {
            const header = await buildCustomerLedgerPdfHeader(cookies, [], [], {
                documentTitle: 'Customer Data Report',
                subtitle: pdfSummaryLines[0] || '',
            });
            const pdfColumns = REPORT_COLUMNS.map((col) => ({
                header: col.headerName,
                dataKey: col.fieldName,
                align: col.align,
            }));
            const blob = createPdfBlob('', pdfColumns, filteredRecords, pdfSummaryLines, header);
            openPdfInApp(blob, {
                title: 'Customer Data Report',
                downloadFileName: `${exportFileBase}.pdf`,
            });
        } catch (error) {
            displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const exportButtons = (
        <ReportExportDropdown
            onCSV={handleCSVExport}
            onExcel={handleExcelExport}
            onPDF={handlePDFExport}
            disabled={isLoading || !filteredRecords.length}
        />
    );

    const toolbarFilters = (
        <>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={salesrepsWithSelectAll}
                            value={selectedSalesrep}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedSalesrep,
                                setSelectedSalesrep,
                                salesreps,
                                salesrepsWithSelectAll,
                            )}
                            placeholder="Sales Rep"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            styles={filterSelectStyles}
                            options={customersWithSelectAll}
                            value={selectedCustomer}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedCustomer,
                                setSelectedCustomer,
                                customers,
                                customersWithSelectAll,
                            )}
                            placeholder="Customer"
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <RangePicker
                            value={[fromDate, toDate]}
                            format="DD-MM-YYYY"
                            onChange={(values) => {
                                if (values?.[0] && values?.[1]) {
                                    setFromDate(values[0]);
                                    setToDate(values[1]);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
                {exportButtons}
                <Button
                    color="primary"
                    className="list-page-toolbar-generate-btn"
                    onClick={loadReport}
                    disabled={isLoading}
                >
                    Generate
                </Button>
            </div>
        </>
    );

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap" className="customer-data-report-page">
                <div className="customer-data-report-shell">
                    <ListPageLayout
                        moduleLabel="REPORTS"
                        title="Customer Data Report"
                        subtitle="Customer billing details with project/site information for the selected filters."
                        modeLabel="REPORT"
                        recordCount={filteredRecords.length}
                        recordLabel="rows"
                        searchText={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Search customer, project, site, GST, mobile..."
                        toolbarFilters={toolbarFilters}
                        toolbarVariant="stacked"
                        reportListLink="/ReportAll"
                    >
                        {summaryCards.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.25,
                                    mb: 2,
                                    px: { xs: 0.5, md: 1 },
                                }}
                            >
                                {summaryCards.map((card, index) => (
                                    <Box
                                        key={card.label}
                                        sx={{
                                            minWidth: 120,
                                            borderRadius: '12px',
                                            px: 1.75,
                                            py: 1.1,
                                            color: '#fff',
                                            background: index % 2 === 0
                                                ? 'linear-gradient(135deg, #186098 0%, #0090D8 55%, #40B8E8 100%)'
                                                : 'linear-gradient(135deg, #0E6A98 0%, #0090D8 55%, #40B8E8 100%)',
                                            boxShadow: '0 8px 20px rgba(0, 144, 216, 0.18)',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.9 }}>
                                            {card.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.2, mt: 0.25 }}>
                                            {card.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <ReactTableWrapper
                            columns={REPORT_COLUMNS}
                            data={filteredRecords}
                            isLoading={isLoading}
                            enableTableSearch={false}
                            compactRows
                            layoutFullWidth
                            defaultPageSize={10}
                        />
                    </ListPageLayout>
                </div>
            </div>
        </div>
    );
}

export default CustomerDataReport;
